import React from "react";
import MultiSelectWithAddition from "components/multiSelect/MultiSelectWithAddition";
import uniqBy from "lodash/uniqBy";
import { type GenericFiltersMap } from "utils/filterUtils";

export interface MultiSelectItem {
  text: string;
  value: string | number;
  [key: string]: unknown;
}

interface FilterBaseProps<T extends string | number> {
  filter: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
  propertyName: T;
  className?: string;
  placeholder?: string;
  id?: string;
}

export interface FilterMultiSelectWithAdditionProps<T extends string | number> extends FilterBaseProps<T> {
  items?: MultiSelectItem[];
  additionLabel?: string;
  additionPosition?: string;
  handleMultiSelectAddition?: (event: React.SyntheticEvent, data: { value: string }) => void;
  handleMultiSelectClick?: (event: React.MouseEvent, data: { value: string | number }) => void;
  [key: string]: unknown;
}

function FilterMultiSelectWithAddition<T extends string | number>(props: FilterMultiSelectWithAdditionProps<T>) {
  const {
    id,
    additionLabel,
    additionPosition,
    handleMultiSelectAddition,
    handleMultiSelectClick,
    className,
    placeholder,
    updateFilter,
    filter,
    propertyName,
  } = props;
  const { items, ...multiSelectProps } = props;

  const onValueChange = ({ value }: { value: (string | number)[] }) => {
    let newFilter = { ...filter };

    if (value.length !== 0) {
      newFilter[propertyName] = value;
    } else {
      delete newFilter[propertyName];
    }

    updateFilter(newFilter);
  };

  const selected = filter[propertyName] ? (filter[propertyName] as (string | number)[]) : [];
  const options = items ? uniqBy([...items, ...selected.map((x) => ({ text: String(x), value: x }))], "value") : null;

  return (
    <MultiSelectWithAddition
      id={id}
      className={className}
      additionLabel={additionLabel}
      additionPosition={additionPosition}
      onAddItem={handleMultiSelectAddition}
      onClick={handleMultiSelectClick}
      selected={selected}
      placeholder={placeholder}
      onMultiSelectChange={onValueChange}
      items={options as MultiSelectItem[] | null}
      {...multiSelectProps}
    />
  );
}

export default FilterMultiSelectWithAddition;
